import { Button, CloseIcon, Dialog, PencilIcon, ToolkitIcon } from '@repo/atomic-components';
import { ToolkitTool } from '@/api';

interface ToolkitPromptPreviewModalProps {
  visible: boolean;
  tool: ToolkitTool | null;
  onHide: () => void;
  onSendToChat: (tool: ToolkitTool) => void;
  onEdit?: (tool: ToolkitTool) => void;
  onRequestGlobalToolkit?: (tool: ToolkitTool) => void;
}

export const ToolkitPromptPreviewModal = ({
  visible,
  tool,
  onHide,
  onSendToChat,
  onEdit,
  onRequestGlobalToolkit,
}: ToolkitPromptPreviewModalProps) => {
  if (!tool) return null;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      closable={false}
      header={
        <div className="text-theme-black flex w-full items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-[1.25rem] leading-7.5 font-semibold">{tool.toolName}</span>
            <span className="text-theme-text-grey text-sm">{tool.description}</span>
          </div>
          <Button
            type="button"
            onClick={onHide}
            outlined
            className="text-theme-black hover:text-theme-black flex h-8 w-8 shrink-0 items-center justify-center rounded border-none transition-colors hover:bg-transparent"
            aria-label="Close"
          >
            <CloseIcon className="h-[14px] w-[14px]" />
          </Button>
        </div>
      }
      className="!w-[min(42.5rem,calc(100vw-48px))]"
      pt={{
        root: {
          className: 'rounded-lg dialog-shadow flex max-h-[min(40rem,calc(100vh-48px))] flex-col',
        },
        header: {
          className:
            'cursor-default !rounded-t-lg bg-theme-white text-theme-black border-theme-border-grey shrink-0 border-b px-6 py-4',
        },
        headerTitle: { className: '!m-0 !w-full' },
        content: {
          className: 'flex min-h-0 flex-1 flex-col overflow-y-auto bg-theme-white px-6 pt-4 pb-6 rounded-b-lg',
        },
        closeButton: { className: 'hidden' },
      }}
    >
      <div className="text-theme-black flex min-h-0 flex-1 flex-col gap-4">
        <div className="text-theme-text-grey min-h-0 flex-1 overflow-y-auto text-sm whitespace-pre-wrap">
          {tool.prompt}
        </div>
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-2">
            {onEdit && (
              <Button
                type="button"
                outlined
                className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex items-center gap-2"
                onClick={() => {
                  onHide();
                  onEdit(tool);
                }}
              >
                <PencilIcon className="h-[16px] w-[16px]" />
                Edit prompt
              </Button>
            )}
            {onRequestGlobalToolkit && (
              <Button
                type="button"
                outlined
                className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex items-center gap-2"
                onClick={() => {
                  onHide();
                  onRequestGlobalToolkit(tool);
                }}
              >
                <ToolkitIcon className="h-[16px] w-[16px]" />
                Add to shared toolkit
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={onHide}
              outlined
              className="text-theme-black border-theme-border-grey hover:bg-theme-background-grey justify-center"
            >
              Dismiss
            </Button>
            <Button
              type="button"
              onClick={() => onSendToChat(tool)}
              className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white justify-center"
            >
              Send to chat
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
